import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiPlus, FiEdit, FiTrash2, FiStar, FiX, FiCheck, FiAlertCircle } from 'react-icons/fi';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';

const AdminMechanics = () => {
  const [mechanics, setMechanics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState(''); // 'add', 'edit', 'delete'
  const [selectedMechanic, setSelectedMechanic] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  
  // Form state
  const [mechanicForm, setMechanicForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    experience: '',
    status: 'active'
  });
  
  // Form validation state
  const [validation, setValidation] = useState({
    name: true,
    email: true,
    password: true,
    experience: true
  });

  const fetchMechanics = useCallback(async () => {
    try {
      setLoading(true);
      
      console.log('Fetching mechanics with params:', {
        page: currentPage,
        limit: 10,
        search: searchQuery || undefined,
      });
      
      // Get mechanics from the API
      const response = await API.get('/api/mechanics');
      console.log('Mechanics API response:', response.data);
      
      let filteredData = response.data;
      
      // Apply filters client-side
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filteredData = filteredData.filter(
          mechanic => 
            mechanic.name?.toLowerCase().includes(query) ||
            mechanic.email?.toLowerCase().includes(query) ||
            mechanic.phone?.toLowerCase().includes(query)
        );
      }
      
      // Calculate pagination
      const totalItems = filteredData.length;
      const calculatedTotalPages = Math.max(1, Math.ceil(totalItems / 10));
      setTotalPages(calculatedTotalPages);
      
      // Apply pagination client-side
      const startIndex = (currentPage - 1) * 10;
      const endIndex = startIndex + 10;
      const paginatedData = filteredData.slice(startIndex, endIndex);
      
      // Map the data to the expected format
      const formattedData = paginatedData.map(mechanic => ({
        id: mechanic.id,
        user_id: mechanic.user_id,
        name: mechanic.name,
        email: mechanic.email || 'N/A',
        phone: mechanic.phone || 'N/A',
        experience: mechanic.experience || 0,
        rating: mechanic.rating || 0,
        status: mechanic.status || 'active',
        avatar_url: mechanic.avatar_url,
        initials: mechanic.initials,
        completedJobs: mechanic.completed_jobs || 0
      }));
      
      setMechanics(formattedData);
      
    } catch (error) {
      console.error('Error fetching mechanics:', error);
      setMechanics([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery]);

  useEffect(() => {
    fetchMechanics();
  }, [fetchMechanics]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchMechanics();
  };

  // Form handling functions
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMechanicForm({
      ...mechanicForm,
      [name]: value
    });
    
    // Clear validation error when user types
    if (['name', 'email', 'password', 'experience'].includes(name) && validation[name] === false) {
      setValidation({
        ...validation,
        [name]: true
      });
    }
  };
  
  const validateForm = () => {
    const isAddMode = modalType === 'add';
    
    const newValidation = {
      name: mechanicForm.name.trim() !== '',
      email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mechanicForm.email),
      password: isAddMode ? mechanicForm.password.length >= 6 : true,
      experience: mechanicForm.experience !== ''
    };
    
    setValidation(newValidation);
    return Object.values(newValidation).every(valid => valid);
  };
  
  const resetForm = () => {
    setMechanicForm({
      name: '',
      email: '',
      password: '',
      phone: '',
      experience: '',
      status: 'active'
    });
    setValidation({
      name: true,
      email: true,
      password: true,
      experience: true
    });
    setFormError('');
  };
  
  const prepareFormForEdit = (mechanic) => {
    setMechanicForm({
      name: mechanic.name || '',
      email: mechanic.email || '',
      password: '', // Don't populate password for security
      phone: mechanic.phone || '',
      experience: mechanic.experience?.toString() || '',
      status: mechanic.status || 'active'
    });
  };
  
  const handleSubmitMechanic = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setFormSubmitting(true);
    setFormError('');
    
    try {
      // Prepare the request data
      const userData = {
        name: mechanicForm.name,
        email: mechanicForm.email,
        role: 'mechanic', // Always set role to mechanic
        status: mechanicForm.status,
        experience: parseInt(mechanicForm.experience),
        phone: mechanicForm.phone
      };
      
      // Add password only if provided (required for new users)
      if (mechanicForm.password) {
        userData.password = mechanicForm.password;
      }
      
      let response;
      
      if (modalType === 'add') {
        // Create new mechanic (which is actually creating a user with mechanic role)
        response = await API.post('/api/user', userData);
        console.log('Mechanic created:', response.data);
      } else {
        // Update existing mechanic
        response = await API.put(`/api/user/${selectedMechanic.user_id}`, userData);
        console.log('Mechanic updated:', response.data);
      }
      
      // Refresh the mechanic list
      fetchMechanics();
      
      // Close the modal and reset form
      closeModal();
      
      // Show success message
      alert(
        modalType === 'add' 
          ? 'Montir berhasil ditambahkan!' 
          : 'Montir berhasil diperbarui!'
      );
    } catch (error) {
      console.error('Error submitting mechanic:', error);
      setFormError(
        error.response?.data?.message || 
        'Terjadi kesalahan saat menyimpan data montir'
      );
    } finally {
      setFormSubmitting(false);
    }
  };
  
  const handleAddMechanic = () => {
    resetForm(); // Reset form before opening
    setModalType('add');
    setSelectedMechanic(null);
    setIsModalOpen(true);
  };

  const handleEditMechanic = (mechanic) => {
    prepareFormForEdit(mechanic);
    setModalType('edit');
    setSelectedMechanic(mechanic);
    setIsModalOpen(true);
  };

  const handleDeleteMechanic = (mechanic) => {
    setModalType('delete');
    setSelectedMechanic(mechanic);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMechanic(null);
  };

  const confirmDelete = async () => {
    try {
      // Delete the user account (which will cascade delete the mechanic record)
      await API.delete(`/api/user/${selectedMechanic.user_id}`);
      fetchMechanics();
      closeModal();
      alert('Montir berhasil dihapus!');
    } catch (error) {
      console.error('Error deleting mechanic:', error);
      alert('Terjadi kesalahan saat menghapus montir');
      closeModal();
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return (
        <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">
          Aktif
        </span>
      );
    } else {
      return (
        <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-semibold">
          Tidak Aktif
        </span>
      );
    }
  };

  const renderPagination = () => {
    return (
      <div className="flex justify-center mt-6">
        <nav className="flex items-center">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Sebelumnya
          </button>
          <div className="px-4 py-1 border-t border-b border-gray-300 bg-white text-sm text-gray-700">
            Halaman {currentPage} dari {totalPages}
          </div>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Berikutnya
          </button>
        </nav>
      </div>
    );
  };

  const renderDeleteModal = () => {
    if (!selectedMechanic) return null;

    return (
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h2 className="text-xl font-bold text-secondary mb-4">Hapus Montir</h2>
        <p className="mb-6">
          Apakah Anda yakin ingin menghapus montir <strong>{selectedMechanic.name}</strong>?
        </p>
        <div className="flex justify-end">
          <button
            onClick={closeModal}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 mr-2"
          >
            Batal
          </button>
          <button
            onClick={confirmDelete}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Hapus
          </button>
        </div>
      </div>
    );
  };
  
  const renderMechanicForm = () => {
    const isAddMode = modalType === 'add';
    const title = isAddMode ? 'Tambah Montir Baru' : 'Edit Montir';
    const submitText = isAddMode ? 'Tambah' : 'Simpan Perubahan';

    return (
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-secondary">{title}</h2>
          <button
            onClick={closeModal}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <FiX size={24} />
          </button>
        </div>

        {formError && (
          <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <div className="flex items-center">
              <FiAlertCircle className="mr-2" />
              <span>{formError}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmitMechanic}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-700 border-b pb-2">Informasi Dasar</h3>
              
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Nama Lengkap <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={mechanicForm.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${
                    validation.name ? 'border-gray-300' : 'border-red-500'
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-primary`}
                  required
                />
                {!validation.name && (
                  <p className="mt-1 text-sm text-red-500">Nama tidak boleh kosong</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={mechanicForm.email}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${
                    validation.email ? 'border-gray-300' : 'border-red-500'
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-primary`}
                  required
                />
                {!validation.email && (
                  <p className="mt-1 text-sm text-red-500">Format email tidak valid</p>
                )}
              </div>

              {/* Password - Required for new users, optional for edit */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password {isAddMode && <span className="text-red-500">*</span>}
                  {!isAddMode && <span className="text-xs text-gray-500">(Biarkan kosong jika tidak ingin mengubah)</span>}
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={mechanicForm.password}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${
                    validation.password ? 'border-gray-300' : 'border-red-500'
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-primary`}
                  required={isAddMode}
                />
                {!validation.password && (
                  <p className="mt-1 text-sm text-red-500">Password minimal 6 karakter</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Nomor Telepon
                </label>
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  value={mechanicForm.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            {/* Professional Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-700 border-b pb-2">Informasi Profesional</h3>
              
              {/* Experience */}
              <div>
                <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-1">
                  Pengalaman (Tahun) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="experience"
                  name="experience"
                  min="0"
                  value={mechanicForm.experience}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${
                    validation.experience ? 'border-gray-300' : 'border-red-500'
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-primary`}
                  required
                />
                {!validation.experience && (
                  <p className="mt-1 text-sm text-red-500">Pengalaman wajib diisi</p>
                )}
              </div>

              {/* Status */}
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={mechanicForm.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="active">Aktif</option>
                  <option value="inactive">Tidak Aktif</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-6 pt-4 border-t">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 mr-2"
              disabled={formSubmitting}
            >
              Batal
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark flex items-center"
              disabled={formSubmitting}
            >
              {formSubmitting ? (
                <>
                  <div className="animate-spin h-4 w-4 mr-2 border-t-2 border-b-2 border-white rounded-full"></div>
                  <span>Menyimpan...</span>
                </>
              ) : (
                <>
                  <FiCheck className="mr-2" />
                  <span>{submitText}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <DashboardLayout userRole="admin" userName="Admin">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-secondary">Manajemen Montir</h1>
          <p className="text-gray-600">Kelola semua montir bengkel</p>
        </div>
        <button
          onClick={handleAddMechanic}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors flex items-center"
        >
          <FiPlus className="mr-2" /> Tambah Montir
        </button>
      </div>

      {/* Search and Filter */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <form onSubmit={handleSearch} className="flex mb-4 md:mb-0">
            <div className="relative">
              <input
                type="text"
                placeholder="Cari nama atau email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-l focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            <button
              type="submit"
              className="bg-primary text-white px-4 py-2 rounded-r hover:bg-primary-dark"
            >
              Cari
            </button>
          </form>
        </div>
      </div>

      {/* Mechanics Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : mechanics.length === 0 ? (
          <div className="text-center py-16">
            <FiPlus size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">Tidak ada montir yang ditemukan</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nama</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kontak</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pengalaman</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aksi</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mechanics.map((mechanic) => (
                  <tr key={mechanic.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">#{mechanic.id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{mechanic.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{mechanic.email}</div>
                      <div className="text-sm text-gray-500">{mechanic.phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{mechanic.experience} tahun</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FiStar className="text-yellow-400 mr-1" />
                        <span className="text-sm text-gray-900">{mechanic.rating}</span>
                        <span className="text-xs text-gray-500 ml-1">({mechanic.completedJobs || 0} job)</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(mechanic.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEditMechanic(mechanic)}
                        className="text-yellow-600 hover:text-yellow-900 mr-3"
                      >
                        <FiEdit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteMechanic(mechanic)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && mechanics.length > 0 && renderPagination()}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          {modalType === 'delete' && renderDeleteModal()}
          {(modalType === 'add' || modalType === 'edit') && renderMechanicForm()}
        </div>
      )}
    </DashboardLayout>
  );
};

export default AdminMechanics; 